import type {TLevelConfig, TLevelObject} from "@repo/shared";
import type {Matrix4, Object3D, Object3DEventMap} from "three";
import * as THREE from "three";

type ChangeCallback = (index: number) => void;

export class InstanceManager {
  public objects: THREE.Object3D[];
  public selectedObjects: Set<number>;
  private changeCallbacks: ChangeCallback[];
  private readonly config: TLevelConfig;

  constructor(config: TLevelConfig) {
    this.config = config;
    this.objects = [];
    this.selectedObjects = new Set<number>();
    this.changeCallbacks = [];
  }

  /**
   * Add a new instance from a LevelObject.
   * Uses the object's position (x,y), rotation, and scale,
   * determines z using the type's zLevel and the object's scale,
   * and stores type & variant in userData.
   *
   * @param levelObject The LevelObject representing the instance to create
   * @param options Options for the instance creation
   * @returns The index of the newly added instance
   */
  addInstance(levelObject: TLevelObject, options = {select: false}): number {
    const obj = new THREE.Object3D();
    const [px, py] = levelObject.position;
    const rz = levelObject.rotation;
    const [sx, sy, sz = 1] = levelObject.scale;

    const typeConfig = this.config.objectTypes[levelObject.type];
    if (!typeConfig) {
      console.warn(this.config);
      throw new Error(`Object type [${levelObject.type}] not found in config.`);
    }

    // Store type & variant in userData
    obj.userData = {
      type: levelObject.type,
      variant: typeConfig.shapeIndex,
      zLevel: typeConfig.zLevel,
      scaleFactor: typeConfig.scaleFactor,
    };

    // We'll initialize scale first to get correct manhattanLength for z positioning.
    obj.scale.set(sx, sy, sz);

    obj.position.set(px, py, this.calculateZValue(obj));
    obj.rotation.set(0, 0, rz);

    obj.updateMatrix();

    this.objects.push(obj);
    const newIndex = this.objects.length - 1;

    if (options.select) this.selectedObjects.add(newIndex);

    this.notifyChange(newIndex);
    return newIndex;
  }

  getAllInstances(): TLevelObject[] {
    return this.objects.map(this.exportInstance.bind(this));
  }

  getSelectedInstances(): TLevelObject[] {
    return Array.from(this.selectedObjects).map((index) => {
      const obj = this.objects[index]!;
      return this.exportInstance(obj);
    });
  }

  applyTransform(matrices: Matrix4[]): void {
    for (const index of this.selectedObjects) {
      const object = this.objects[index]!;
      for (const matrix of matrices) {
        this.applyMatrix(object, matrix);
      }
      this.notifyChange(index);
    }
  }

  public applyMatrices(index: number, matrices: Matrix4[]) {
    if (index < 0 || index > this.objects.length)
      throw new Error(`Object index [${index}] is out of range!`);
    const object = this.objects[index]!;
    for (const matrix of matrices) {
      this.applyMatrix(object, matrix);
    }
    this.notifyChange(index);
  }

  public setMatrix(index: number, matrix: Matrix4): void {
    if (index < 0 || index > this.objects.length)
      throw new Error(`Object index [${index}] is out of range!`);
    const object = this.objects[index]!;
    object.matrix.copy(matrix);
    object.matrix.decompose(object.position, object.quaternion, object.scale);
    this.notifyChange(index);
  }

  private applyMatrix(object: Object3D<Object3DEventMap>, matrix: Matrix4) {
    object.applyMatrix4(matrix);
    object.position.setZ(this.calculateZValue(object));
    object.updateMatrix();
  }

  isSelected(index: number): boolean {
    return index !== -1 && this.selectedObjects.has(index);
  }

  select(index: number): void {
    if (index >= 0 && index < this.objects.length) {
      this.selectedObjects.add(index);
      // this.notifyChange(index);
    }
  }

  unselect(index: number): void {
    if (this.selectedObjects.has(index)) {
      this.selectedObjects.delete(index);
      // this.notifyChange(index);
    }
  }

  clearSelection(): void {
    // const prevSelectedIndices = new Set(this.selectedObjects);
    this.selectedObjects.clear();
    // for (const index of prevSelectedIndices) {
    //    this.notifyChange(index);
    // }
  }

  getSelection(): THREE.Object3D[] {
    return Array.from(this.selectedObjects).map(
      (index) => this.objects[index]!,
    );
  }

  removeInstance(index: number): void {
    if (index >= 0 && index < this.objects.length) {
      const lastIndex = this.objects.length - 1;
      this.selectedObjects.delete(index);

      if (index !== lastIndex) {
        // Move the last object to the removed spot
        this.objects[index] = this.objects[lastIndex]!;
        this.objects.pop();

        // Update selected objects if last index was selected
        if (this.selectedObjects.delete(lastIndex)) {
          this.selectedObjects.add(index);
        }
      } else {
        this.objects.pop();
      }
      this.notifyChange(index);
    }
  }

  clear(): void {
    this.objects = [];
    this.selectedObjects.clear();
    this.notifyChange(0);
  }

  registerChangeCallback(callback: ChangeCallback): () => void {
    this.changeCallbacks.push(callback);
    return () => {
      this.changeCallbacks = this.changeCallbacks.filter(
        (cb) => cb !== callback,
      );
    };
  }

  private exportInstance(obj: Object3D) {
    return {
      type: obj.userData.type,
      position: [obj.position.x, obj.position.y],
      rotation: obj.rotation.z,
      scale: [obj.scale.x, obj.scale.y],
    } as TLevelObject;
  }

  public notifyChange(index: number): void {
    for (const callback of this.changeCallbacks) {
      callback(index);
    }
  }

  private calculateZValue(obj: Object3D) {
    const zLevel = obj.userData.zLevel ?? 1;
    const scaleFactor = obj.userData.scaleFactor ?? 0;
    const minScale = Math.min(obj.scale.x, obj.scale.y);
    const scaleOffset = minScale * scaleFactor;
    return zLevel - scaleOffset;
  }
}
